import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Search, Users } from 'lucide-react';
import * as api from '../../lib/api';
import { formatCents } from '../../lib/format';

const PAGE_SIZE = 50;

/**
 * Tenant directory.
 *
 * Scoped server-side: an admin sees the people renting at the yards they run,
 * the owner sees everyone. A tenant who has never held a lease is only visible
 * to the owner, because no admin "owns" them.
 */
export default function TenantsPage() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  // The directory defaults to everyone — you come here to look someone up, and
  // that someone may have moved out. The dashboard defaults the other way.
  const [currentOnly, setCurrentOnly] = useState(false);
  const [data, setData] = useState({ tenants: [], total: 0 });
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);

  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let live = true;
    setState('loading');
    api.adminTenants({
      q: query || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE,
      current: currentOnly || undefined,
    })
      .then((payload) => { if (live) { setData(payload); setState('ready'); } })
      .catch((err) => { if (live) { setError(err.message); setState('error'); } });
    return () => { live = false; };
  }, [query, page, currentOnly]);

  const pages = useMemo(() => Math.max(1, Math.ceil(data.total / PAGE_SIZE)), [data.total]);

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
      <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Tenants</h1>
      <p className="mt-1 text-sm text-slate-600">
        Everyone renting space at the facilities you run.
      </p>

      {/* The count is the only visible evidence the filter did anything. It
          used to live inside the pagination bar, which hides itself when
          everything fits on one page — so with thirteen tenants and a
          fifty-row page, switching filters silently removed two rows from the
          middle of a list and showed no number that changed. */}
      {state === 'ready' && (
        <p aria-live="polite" className="mt-3 text-sm font-bold text-slate-900">
          {data.total} {currentOnly ? 'renting now' : data.total === 1 ? 'tenant' : 'tenants'}
          {query && <span className="font-normal text-slate-500"> matching &ldquo;{query}&rdquo;</span>}
          {currentOnly && !query && (
            <span className="font-normal text-slate-500"> · past tenants hidden</span>
          )}
        </p>
      )}

      <div className="mt-5 flex rounded-lg border border-slate-300 p-0.5 sm:w-fit" role="group" aria-label="Filter tenants">
        {[[false, 'Everyone'], [true, 'Renting now']].map(([value, label]) => (
          <button key={label} type="button" onClick={() => { setCurrentOnly(value); setPage(0); }}
                  aria-pressed={currentOnly === value}
                  className={`flex-1 rounded-md px-3 py-1.5 text-xs font-bold sm:flex-none ${
                    currentOnly === value ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-500" aria-hidden="true" />
        <label className="sr-only" htmlFor="tenant-search">Search tenants by name or email</label>
        <input id="tenant-search" type="search" className="field-input pl-9"
               placeholder="Search by name or email" value={search}
               onChange={(e) => setSearch(e.target.value)} />
      </div>

      {state === 'error' && (
        <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-4 text-sm text-rose-800">{error}</p>
      )}

      {state === 'loading' && (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-brand-600" aria-hidden="true" />
        </div>
      )}

      {state === 'ready' && data.tenants.length === 0 && (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white py-16 text-center">
          <Users className="mx-auto h-8 w-8 text-brand-300" aria-hidden="true" />
          <p className="mt-3 text-sm font-bold text-slate-900">
            {query ? 'No tenants match that search'
              : currentOnly ? 'Nobody is renting right now'
              : 'No tenants yet'}
          </p>
          <p className="mt-1 text-sm text-slate-500">
            {query
              ? (currentOnly
                  ? 'Nobody currently renting matches. They may have moved out.'
                  : 'Try part of a last name or an email address.')
              : currentOnly
                ? 'Past tenants are still on file.'
                : 'Tenants appear here once they reserve a space.'}
          </p>
          {currentOnly && (
            <button type="button" onClick={() => { setCurrentOnly(false); setPage(0); }}
                    className="mt-2 text-xs font-bold text-brand-700 hover:underline">
              Search everyone instead
            </button>
          )}
        </div>
      )}

      {state === 'ready' && data.tenants.length > 0 && (
        <>
          <div className="mt-5 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[44rem] text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-left text-[12px] font-bold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-2.5">Tenant</th>
                  <th className="px-4 py-2.5">Facility</th>
                  <th className="px-4 py-2.5 text-right">Leases</th>
                  <th className="px-4 py-2.5 text-right">Outstanding</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.tenants.map((t) => (
                  <tr key={t.public_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-900">{t.first_name} {t.last_name}</p>
                      <p className="text-xs text-slate-500">{t.email}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.facility_name ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-900">
                      {t.active_leases}
                    </td>
                    <td className={`px-4 py-3 text-right font-semibold tabular-nums ${
                      Number(t.outstanding_cents) > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                      {formatCents(Number(t.outstanding_cents))}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/admin/tenants/${t.public_id}`}
                            className="text-xs font-bold text-brand-700 hover:underline">
                        Open →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <nav className="mt-4 flex items-center justify-between" aria-label="Tenant pages">
              <p className="text-xs text-slate-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)}
              </p>
              <div className="flex gap-2">
                <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  Previous
                </button>
                <button type="button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                  Next
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  );
}
